import * as React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { BrandCard, type BrandCardProps } from "./BrandCard";

/**
 * AnimatedCard Component
 *
 * Interactive card with hover animations:
 * - Scale effect on hover/tap
 * - Simple shadow on hover
 */

export interface AnimatedCardProps extends BrandCardProps {
  /**
   * Enable scale on hover
   */
  enableScale?: boolean;

  /**
   * Enable lift on hover
   */
  enableLift?: boolean;

  /**
   * Animation duration
   */
  duration?: number;

  /**
   * Click handler
   */
  onClick?: () => void;
}

export const AnimatedCard = React.forwardRef<HTMLDivElement, AnimatedCardProps>(
  (
    {
      children,
      className,
      enableScale = true,
      enableLift = true,
      duration = 0.3,
      onClick,
      hoverable = true,
      glow = "subtle",
      ...props
    },
    ref,
  ) => {
    return (
      <motion.div
        className={cn(onClick && "cursor-pointer")}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
      >
        <BrandCard
          ref={ref}
          className={cn(
            "transition-all duration-300",
            enableLift && "hover:-translate-y-1 hover:shadow-card-hover",
            className,
          )}
          hoverable={hoverable}
          glow={glow}
          {...props}
        >
          {children}
        </BrandCard>
      </motion.div>
    );
  },
);

AnimatedCard.displayName = "AnimatedCard";

/**
 * HoverGlowCard - Card with enhanced glow on hover
 */
export interface HoverGlowCardProps extends BrandCardProps {
  /**
   * Glow color
   */
  glowColor?: "gold" | "emerald" | "custom";

  /**
   * Custom glow color (CSS color)
   */
  customGlowColor?: string;
}

export const HoverGlowCard = React.forwardRef<
  HTMLDivElement,
  HoverGlowCardProps
>(
  (
    { children, className, glowColor = "gold", customGlowColor, ...props },
    ref,
  ) => {
    const glowColors = {
      gold: "rgba(30, 64, 175, 0.15)",
      emerald: "rgba(30, 64, 175, 0.15)",
      custom: customGlowColor || "rgba(30, 64, 175, 0.15)",
    };

    return (
      <motion.div
        className="relative"
        whileHover={{
          boxShadow: `0 0 40px ${glowColors[glowColor]}`,
          transition: { duration: 0.3 },
        }}
      >
        <BrandCard ref={ref} className={className} glow="strong" {...props}>
          {children}
        </BrandCard>
      </motion.div>
    );
  },
);

HoverGlowCard.displayName = "HoverGlowCard";

/**
 * FlipCard - Card with flip animation
 */
export interface FlipCardProps {
  /**
   * Front content
   */
  front: React.ReactNode;

  /**
   * Back content
   */
  back: React.ReactNode;

  /**
   * Container class name
   */
  className?: string;

  /**
   * Card props for both sides
   */
  cardProps?: BrandCardProps;
}

export const FlipCard = ({
  front,
  back,
  className,
  cardProps,
}: FlipCardProps) => {
  const [isFlipped, setIsFlipped] = React.useState(false);

  return (
    <div className={cn("perspective-1000", className)}>
      <motion.div
        className="relative w-full h-full"
        style={{ transformStyle: "preserve-3d" }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        onClick={() => setIsFlipped(!isFlipped)}
      >
        {/* Front */}
        <div
          className={cn(
            "absolute inset-0 backface-hidden",
            isFlipped && "pointer-events-none",
          )}
          style={{ backfaceVisibility: "hidden" }}
        >
          <BrandCard hoverable {...cardProps}>
            {front}
          </BrandCard>
        </div>

        {/* Back */}
        <div
          className={cn(
            "absolute inset-0 backface-hidden",
            !isFlipped && "pointer-events-none",
          )}
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <BrandCard hoverable {...cardProps}>
            {back}
          </BrandCard>
        </div>
      </motion.div>
    </div>
  );
};

FlipCard.displayName = "FlipCard";

/**
 * PulsingCard - Card with pulsing animation
 */
export interface PulsingCardProps extends BrandCardProps {
  /**
   * Pulse speed (seconds per cycle)
   */
  pulseSpeed?: number;
}

export const PulsingCard = React.forwardRef<HTMLDivElement, PulsingCardProps>(
  ({ children, className, pulseSpeed = 2, ...props }, ref) => {
    return (
      <motion.div
        animate={{
          boxShadow: [
            "0 0 20px rgba(30, 64, 175, 0.15)",
            "0 0 40px rgba(30, 64, 175, 0.15)",
            "0 0 20px rgba(30, 64, 175, 0.15)",
          ],
        }}
        transition={{
          duration: pulseSpeed,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <BrandCard ref={ref} className={className} {...props}>
          {children}
        </BrandCard>
      </motion.div>
    );
  },
);

PulsingCard.displayName = "PulsingCard";
